// Thin client for the same-origin /api/ai/* contract (AI-UI.1). Every URL here is a relative
// string literal — never the mock gateway's own host/port — so the dev-server proxy (AI-UI.4)
// and the production build guard (AI-UI.11) both keep working.

export const AI_REQUEST_TIMEOUT_MS = 30000;

// Cloud's backend accepts the session only via a Bearer token (server/middlewares/auth.js) —
// there is no cookie-based session minted for this service yet — so every /api/ai/* call must
// attach it the same way client/src/api/axios.js's request interceptor does for every other call.
function authHeaders() {
  const token = localStorage.getItem('cloudToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function createConversation() {
  const response = await fetch('/api/ai/conversations', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    throw new Error(`createConversation failed with status ${response.status}`);
  }
  return response.json();
}

// Splits an accumulated SSE text buffer on the frame separator ("\n\n"), parsing the JSON
// payload out of each complete frame's `data:` line(s). Returns the parsed events plus
// whatever incomplete trailing text should be kept and prefixed to the next chunk.
export function parseSseBuffer(buffer) {
  const events = [];
  let searchFrom = 0;
  let boundary = buffer.indexOf('\n\n', searchFrom);
  while (boundary !== -1) {
    const frame = buffer.slice(searchFrom, boundary);
    searchFrom = boundary + 2;
    const dataLines = frame.split('\n').filter((line) => line.startsWith('data:'));
    if (dataLines.length > 0) {
      const jsonText = dataLines.map((line) => line.slice(5).trimStart()).join('\n');
      try {
        events.push(JSON.parse(jsonText));
      } catch (err) {
        // Malformed frame — drop it rather than crash the stream.
      }
    }
    boundary = buffer.indexOf('\n\n', searchFrom);
  }
  return { events, remainder: buffer.slice(searchFrom) };
}

// Sends a message and streams the reply via `onEvent(frame)`. Transparently handles both the
// SSE transport and the documented non-streaming JSON fallback (AI-UI.1) so the caller's event
// handling stays transport-agnostic. Throws on a non-2xx response or a network/abort failure.
export async function sendMessage({ conversationId, content, signal, onEvent }) {
  const response = await fetch('/api/ai/messages', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...authHeaders(),
    },
    body: JSON.stringify({ conversationId, content }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`sendMessage failed with status ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const body = await response.json();
    onEvent({ event: 'message.delta', delta: body.content || '' });
    for (const citation of body.citations || []) {
      onEvent({ event: 'citation', ...citation });
    }
    onEvent({ event: 'done', finishReason: 'stop' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawTerminalEvent = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, remainder } = parseSseBuffer(buffer);
    buffer = remainder;
    for (const evt of events) {
      onEvent(evt);
      if (evt && (evt.event === 'done' || evt.event === 'error')) {
        sawTerminalEvent = true;
      }
    }
  }

  // A stream can close (reader signals done) without the server ever sending
  // a done/error frame (dropped connection, proxy timeout, truncated response).
  // Treat that as a failure so the caller's existing error handling takes over,
  // instead of resolving successfully and leaving the panel stuck mid-stream.
  if (!sawTerminalEvent) {
    throw new Error('sendMessage stream ended without a terminal event');
  }
}

// GET carries the same Bearer auth as every other /api/ai/* call in this file. The request
// carries only the identifier the caller passes in — never raw page data.
export async function getIssueInsight({ entityType, entityId, signal }) {
  const params = new URLSearchParams({ entityType, entityId });
  const response = await fetch(`/api/ai/issue-insight?${params.toString()}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { ...authHeaders() },
    signal,
  });

  if (response.status === 404) {
    const body = await response.json();
    return { notFound: true, message: body.message };
  }

  if (!response.ok) {
    throw new Error(`getIssueInsight failed with status ${response.status}`);
  }

  return response.json();
}

// Fire-and-forget feedback on an assistant message; a state-changing POST, so it carries the
// same Bearer auth as createConversation/sendMessage above.
export async function submitFeedback({ messageId, rating, comment }) {
  const response = await fetch('/api/ai/feedback', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ messageId, rating, ...(comment ? { comment } : {}) }),
  });
  if (!response.ok) {
    throw new Error(`submitFeedback failed with status ${response.status}`);
  }
}

// GET carries the same Bearer auth as every other /api/ai/* call in this file.
export async function getNotifications({ signal } = {}) {
  const response = await fetch('/api/ai/notifications', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { ...authHeaders() },
    signal,
  });

  if (!response.ok) {
    throw new Error(`getNotifications failed with status ${response.status}`);
  }

  const body = await response.json();
  return body.notifications;
}

// GET carries the same Bearer auth as every other /api/ai/* call in this file. A body without
// the contract's array is treated as a failure so malformed data never renders.
export async function listConversations({ signal } = {}) {
  const response = await fetch('/api/ai/conversations', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { ...authHeaders() },
    signal,
  });

  if (!response.ok) {
    throw new Error(`listConversations failed with status ${response.status}`);
  }

  const body = await response.json();
  if (!body || !Array.isArray(body.conversations)) {
    throw new Error('listConversations returned a malformed body');
  }
  return body.conversations;
}

// Sends only the conversation id; tenant scope comes from the Bearer session.
export async function listMessages(conversationId, { signal } = {}) {
  const response = await fetch(`/api/ai/messages?conversationId=${encodeURIComponent(conversationId)}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { ...authHeaders() },
    signal,
  });

  if (!response.ok) {
    throw new Error(`listMessages failed with status ${response.status}`);
  }

  const body = await response.json();
  if (!body || !Array.isArray(body.messages)) {
    throw new Error('listMessages returned a malformed body');
  }
  return body.messages;
}
